<template>
    <section class="how-to-use-cloud">
        <div class="main">
            <h2>{{ $t('homepage.howToUseCloud.title') }}</h2>

            <!-- PC: one column per step, icon+connector on top, text below -->
            <div class="pc-layout">
                <div class="steps-row">
                    <div
                        v-for="(step, index) in pcSteps"
                        :key="index"
                        class="step-item"
                    >
                        <div class="step-top">
                            <div class="step-icon">
                                <img :src="step.icon" :alt="step.stepTitle" />
                            </div>
                            <div
                                v-if="index < pcSteps.length - 1"
                                class="step-connector"
                            ></div>
                        </div>
                        <h3 class="step-title" v-html="step.stepTitle"></h3>
                        <p class="step-desc" v-html="step.description"></p>
                    </div>
                </div>
            </div>

            <!-- M: carousel with dots and peek -->
            <div class="m-layout">
                <Carousel
                    :items="1"
                    :dots="true"
                    :nav="false"
                    :loop="false"
                    :minHeight="0"
                    :stagePadding="30"
                    :mouseDrag="true"
                >
                    <div v-for="(step, index) in mSteps" :key="index" class="carousel-card">
                        <div class="card-icon">
                            <img :src="step.icon" :alt="step.stepTitle" />
                        </div>
                        <div class="card-text">
                            <h3 class="step-title" v-html="step.stepTitle"></h3>
                            <p class="step-desc" v-html="step.description"></p>
                        </div>
                    </div>
                </Carousel>
            </div>

            <!-- Pad: carousel, auto-width items, mouse drag -->
            <div class="pad-layout">
                <Carousel
                    items="1"
                    :autoWidth="true"
                    :dots="false"
                    :nav="false"
                    :loop="false"
                    :minHeight="0"
                    :mouseDrag="true"
                >
                    <div v-for="(step, index) in mSteps" :key="index" class="carousel-card pad-card">
                        <div class="card-icon">
                            <img :src="step.icon" :alt="step.stepTitle" />
                        </div>
                        <div class="card-text">
                            <h3 class="step-title" v-html="step.stepTitle"></h3>
                            <p class="step-desc" v-html="step.description"></p>
                        </div>
                    </div>
                </Carousel>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import Vue from 'vue';
import { config } from 'CONFIG';
import { isApp } from '@/utils/business';
import Carousel from '@/basic/Carousel.vue';
import PcStep1 from 'IMAGE/homepage/howToUseCloud/pc-step-1.png';
import PcStep2 from 'IMAGE/homepage/howToUseCloud/pc-step-2.png';
import PcStep3 from 'IMAGE/homepage/howToUseCloud/pc-step-3.png';
import PcStep4 from 'IMAGE/homepage/howToUseCloud/pc-step-4.png';
import MStep1 from 'IMAGE/homepage/howToUseCloud/m-step-1.png';
import MStep2 from 'IMAGE/homepage/howToUseCloud/m-step-2.png';
import MStep3 from 'IMAGE/homepage/howToUseCloud/m-step-3.png';
import MStep4 from 'IMAGE/homepage/howToUseCloud/m-step-4.png';

const pcIcons = [PcStep1, PcStep2, PcStep3, PcStep4];
const mIcons = [MStep1, MStep2, MStep3, MStep4];

interface IStep {
    icon: string;
    stepTitle: string;
    description: string;
}

export default Vue.extend({
    name: 'HowToUseCloud',
    components: { Carousel },
    computed: {
        pcSteps(): IStep[] {

            return this.buildSteps( pcIcons );

        },
        mSteps(): IStep[] {

            return this.buildSteps( mIcons );

        },
        lang(): string {

            return this.$store.getters.lang;

        },
        isApp(): boolean {

            return isApp;

        },
    },
    methods: {
        buildSteps( icons: string[] ): IStep[] {

            const lang = this.lang;
            const app = this.isApp;
            return [
                {
                    icon: icons[0],
                    stepTitle: this.$t( 'homepage.howToUseCloud.step1.title' ).toString(),
                    description: this.$t(
                        app ? 'homepage.howToUseCloud.step1.appDesc' : 'homepage.howToUseCloud.step1.desc',
                        lang,
                        { link: `${config.reolink}/software-and-manual/`, target: '_self' }
                    ).toString()
                },
                {
                    icon: icons[1],
                    stepTitle: this.$t( 'homepage.howToUseCloud.step2.title' ).toString(),
                    description: this.$t( 'homepage.howToUseCloud.step2.desc' ).toString()
                },
                {
                    icon: icons[2],
                    stepTitle: this.$t( 'homepage.howToUseCloud.step3.title' ).toString(),
                    description: this.$t(
                        'homepage.howToUseCloud.step3.desc',
                        lang,
                        { link: '/user/dashboard', target: '_self' }
                    ).toString()
                },
                {
                    icon: icons[3],
                    stepTitle: this.$t( 'homepage.howToUseCloud.step4.title' ).toString(),
                    description: this.$t( 'homepage.howToUseCloud.step4.desc' ).toString()
                }
            ];

        }
    }
});
</script>

<style lang="less" scoped>
.how-to-use-cloud {
    background-color: #FFFFFF;
    padding: 100px 0;

    @media @mobile {
        padding: 48px 0;
    }

    .main {
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;

        @media @mobile {
            padding: 0 20px;
        }

        h2 {
            font-size: 48px;
            font-weight: 600;
            line-height: 62.4px;
            color: @font_primary;
            text-align: center;
            margin-bottom: 48px;

            @media @mobile {
                font-size: 28px;
                line-height: 36.4px;
                margin-bottom: 40px;
            }
        }
    }
}

/* PC layout — one column per step */
.pc-layout {
    display: block;

    @media @mobile {
        display: none;
    }

    .steps-row {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        gap: 48px;
    }

    .step-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .step-top {
        display: flex;
        align-items: center;
        gap: 48px;
        width: 100%;
        margin-bottom: 20px;
    }

    .step-icon {
        width: 100px;
        height: 100px;
        flex: none;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .step-connector {
        flex: 1;
        height: 1px;
        background-image: repeating-linear-gradient(
            to right,
            @font_primary 0,
            @font_primary 4px,
            transparent 4px,
            transparent 8px
        );
    }

    .step-title {
        font-size: 22px;
        font-weight: 500;
        line-height: 28.6px;
        color: @font_primary;
        margin-bottom: 10px;
        text-align: left;
        align-self: stretch;
    }

    .step-desc {
        font-size: 14px;
        font-weight: 400;
        line-height: 19.6px;
        color: @font_primary;
        text-align: left;
        align-self: stretch;

        /deep/ a {
            color: @brand_light;
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        }
    }
}

/* Shared card styles for M and Pad */
.m-layout .carousel-card,
.pad-layout .carousel-card {
    display: flex;
    align-items: flex-start;
    gap: 16px;

    .card-icon {
        width: 64px;
        height: 64px;
        flex: none;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .card-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .step-title {
            font-size: 18px;
            font-weight: 500;
            line-height: 25.2px;
            color: @font_primary;
        }

        .step-desc {
            font-size: 14px;
            font-weight: 400;
            line-height: 19.6px;
            color: @font_secondary;

            /deep/ a {
                color: @brand_light;
                text-decoration: none;
                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
}

/* M layout — Carousel with dots and peek */
.m-layout {
    display: none;

    @media (max-width: 743px) {
        display: block;
    }

    /deep/ .container .owl-carousel {
        width: 100%;
    }

    .carousel-card {
        padding: 0 20px;
    }

    /* owl dots */
    /deep/ .owl-dots {
        display: flex;
        gap: 8px;
        margin-top: 24px;
        justify-content: center;

        .owl-dot {
            width: 8px;
            height: 8px;
            border: none;
            padding: 0;
            background-color: #D8D8D8;
            border-radius: 100%;

            &.active {
                background-color: @brand_light;
            }

            span {
                display: none;
            }
        }
    }
}

/* Pad layout — Carousel autoWidth, mouse drag */
.pad-layout {
    display: none;

    @media (min-width: 744px) and (max-width: 1199px) {
        display: block;
    }

    .pad-card {
        width: 380px;
    }
}
</style>
